import * as React from 'react';
import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Language, Variable } from '@restworld/utility-types';
import { Heading, Button } from '@restworld/ui-ds-base';
import { TrashIcon } from '@heroicons/react/24/outline';

import { WizardSelectField, WizardMultiSelect } from '../';
import { WizardStepProps } from '../../worker-application-wizard.types';
import { useErrorMessage } from '../../wizard-application-wizard.utils';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';

const rootKey = 'languages';
const defaultValue = {};
// language: '',
// language_level_cefr: '',

export const WizardNinthStep: React.FunctionComponent<WizardStepProps> = () => {
  const { control, trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: rootKey,
  });
  useEffect(() => {
    if (!fields.length) append(defaultValue);
  }, [append, fields.length]);
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4 md:h-[26rem] md:overflow-y-auto md:px-1">
      {fields.map((field, fieldIndex) => (
        <React.Fragment key={field.id}>
          <div className="flex justify-start gap-4">
            <Button
              disabled={fields.length === 1}
              onClick={() => remove(fieldIndex)}
              variant="secondary-danger"
              StartIcon={TrashIcon}
            />
            <Heading>{`${t('common:languages')} ${fieldIndex + 1}`}</Heading>
          </div>
          <Fields fieldIndex={fieldIndex} />
          {Boolean(fields.length - 1 === fieldIndex) && (
            <div className="self-center">
              <Button
                onClick={async (e) => {
                  e?.stopPropagation();
                  await trigger(`${rootKey}.${fieldIndex}`)
                    .then((r) => {
                      if (r) append(defaultValue, { focusIndex: fieldIndex + 1 });
                    })
                    .catch((e) => console.error(e, 'error on form validation'));
                }}
                variant="secondary-restworld"
              >
                + {t('onboarding-page:add_languages')}
              </Button>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
export default WizardNinthStep;
const Fields = ({ fieldIndex }: { fieldIndex: number }) => {
  const {
    fieldOptions: { languages, languageLevelCertificate },
  } = useWorkerApplicationCxt();
  return (
    <>
      <Languages fieldIndex={fieldIndex} options={languages} />
      <LanguageLevelCertificate fieldIndex={fieldIndex} options={languageLevelCertificate} />
    </>
  );
};
const Languages = ({ options, fieldIndex }: { options: Variable[]; fieldIndex: number }) => {
  const { getValues } = useFormContext();
  const errorMessage = useErrorMessage({
    rootKey,
    fieldIndex,
    id: 'language',
  });
  const languages: Language[] = getValues(`${rootKey}`);
  const selected = getValues(`${rootKey}.${fieldIndex}.language`);
  const selectedLanguages = languages?.map((v) => v.language).filter((v) => v !== selected);
  const optionsCopy = [...options].filter((v) => !selectedLanguages.includes(v.id as string));
  const { t } = useTranslation();
  return (
    <WizardSelectField
      options={optionsCopy}
      placeholder={t('onboarding-page:choose_language')}
      id={`${rootKey}.${fieldIndex}.language`}
      formLabel={t('common:speech')}
      mainTitle={t('common:speech')}
      rules={{ required: true }}
      errorMessage={errorMessage}
      testId="worker-language-options"
      inputTestId="worker-language-input"
    />
  );
};
const LanguageLevelCertificate = ({ options, fieldIndex }: { options: Variable[]; fieldIndex: number }) => {
  const { getValues } = useFormContext();
  const id = `${rootKey}.${fieldIndex}.language_level_cefr`;
  const language = getValues(`${rootKey}.${fieldIndex}.language`);
  const errorMessage = useErrorMessage({
    rootKey,
    fieldIndex,
    id: 'language_level_cefr',
  });
  const { t } = useTranslation();
  return (
    <WizardMultiSelect
      disabled={!language}
      options={options}
      placeholder={t('onboarding-page:choose_language')}
      id={id}
      key={id}
      formLabel={t('onboarding-page:knowledge_level')}
      rules={{ required: true }}
      errorMessage={errorMessage}
      isModal={false}
      isSingleSelect
      testId="worker-language-level-options"
    />
  );
};
